/**
 * BlackHoleLens — a non-rotating black hole with a thin accretion disc, in TSL (shader gaps K, 2026-09-30).
 *
 * After E. Bruneton 2020 ("Real-time High-Quality Rendering of Non-Rotating Black Holes", arXiv 2010.08735): every
 * pixel's ray is reduced to its plane, and two precomputed tables (SchwarzschildMath: D(e², u) the deflection, U(e², φ)
 * the inverse radius) give, in constant time, where it leaves for the sky (or that it falls in) and where it crosses
 * the disc — the photon ring, the secondary images and the Einstein ring fall out of the tables. The TraceRay port and
 * the disc Doppler factor are Bruneton's MATH (BSD-3-Clause — the notice and licence are in SchwarzschildMath.ts,
 * beside the CPU mirror of every line here); no GLSL is pasted.
 *
 * THE COMPOSITION — bounded, never a fullscreen image filter:
 *   1. THE SKY PASS. `backgroundNode()` is the caller's sky looked up along the BENT direction (the whole celestial
 *      sphere is lensed — weak lensing reaches everywhere, 2 r_s / b, and a hard zone edge would tear stars), for
 *      `scene.backgroundNode`. Geometry is drawn over the background as always: a ship is never warped, the HUD is
 *      never touched, and no raw depth is compared anywhere (THE DEPTH CONVENTION holds on standard and reversed depth).
 *   2. THE BODY. `body` — a camera-centred spherical cap at the hole's distance, covering only the shadow + the disc's
 *      images (`coreAngle`), opaque and depth-writing, shading the SAME function plus the disc: a ship in front is drawn
 *      over it, anything behind the hole is hidden by it, and its edge meets the sky pass seamlessly.
 *   The hole's position is RENDER space (the caller's absolute − origin, in doubles); only camera-relative vectors
 *   reach the GPU (the FP64 floating-origin law).
 *
 * THE STATIC FRAME: the camera is a static observer at u = r_s / d; its view angle is squashed to the orbit equation's
 * coordinate angle (`bhCoordinateDelta`) — the shadow's edge lands at sin α = b_c·u·√(1 − u).
 *
 * THE DISC: Keplerian gas between the ISCO (3 r_s) and `discOuter`, T⁴ ∝ u³(1 − √(3u)) (zero torque at the ISCO), a
 * blackbody chroma LUT (Planck × CIE 1931 → linear sRGB, max channel 1 — the engine's LUT convention), the frequency
 * ratio g (gravitational redshift at both ends + the Doppler of the orbit) shifting the temperature T → g·T and the
 * radiance by g⁴ (bolometric beaming): the approaching side is brighter and bluer. `beaming` 0 is the art-directed
 * Interstellar choice (no shift); the default is physical. The disc's texture is advected at Ω ∝ r^(−3/2) (the
 * Keplerian SHAPE at an art-directed rate — the real one at a game-sized hole is a blur of kHz).
 *
 * THE GAS (realism pass, 2026-10-07 — SchwarzschildMath.BH_GAS holds the numbers): trailing arcs of gradient noise in
 * the co-rotating, pre-wound coordinates (ψ, ln r), a cascade of squared factors (bright knots on a dimmer ground, mean 1),
 * two flow phases cross-faded at constant contrast and each replaced before the shear rewinds it. BAND-LIMITED: every
 * band fades to its mean as one pixel comes to span a noise cell (the footprint is ANALYTIC — the screen derivatives
 * of the hit point, turned into d(azimuth) and d(ln r), plus the shear), and the radial T⁴ profile is taken as its
 * MEAN over the inverse radii the pixel covers (closed form): a disc a few pixels across keeps its flux and shows no
 * texture it cannot resolve.
 *
 * THE CALLER'S BODIES (2026-10-08): a sun is not part of the sky builders — it has a PLACE. `sky.sources(ray)` hands the
 * caller the pixel's escape direction AND a point of its escape asymptote (hole-relative, r_s), so a body a finite
 * distance behind the hole is lensed exactly (the lens equation's D_ls/D_s falls out of the line, not of a formula);
 * `behind` joins the sky before the shadow and the disc, `front` is composited over both (a body nearer than the hole
 * is never bent). Both passes run the same builder, so the sun's images cross the cap's edge without a seam. The CPU
 * oracle is `bhImageAngle` / `bhSourceMiss` (through the tables) and `bhWeakImages` / `bhRingOfDisc` (closed forms).
 *
 * THE BODY'S SEAT: the cap is drawn no farther than `bhDepthSafeDistance` — where it still holds depth codes against
 * the far plane (a sky pass drawn after the opaques, depth-tested, otherwise paints over it: the "QR code" disc).
 *
 * Laws obeyed: every derivative and texture sample is `.toVar()`'d at the top of its Fn (THE LAZY-SAMPLE LAW and
 * WGSL uniformity); positionPrevious is assigned in the body's positionNode (the positionPrevious LAW); tables are
 * fetched with textureLoad + a manual bilinear (float32 is not filterable everywhere).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, float, int, vec2, vec3, vec4, ivec2, uniform, texture, clamp, min, max, sqrt, log, sin, cos, asin, atan,
  abs, floor, fract, mod, sign, step, select, cross, dot, normalize, length, fwidth, dFdx, dFdy, smoothstep, pow, mix,
  positionGeometry, positionWorld, positionWorldDirection, positionPrevious, cameraPosition, mx_noise_float,
} from 'three/tsl';
import {
  BH_MU, BH_ISCO_R, BH_CRITICAL_B, BH_BLACKBODY_LUT_SIZE, BH_BLACKBODY_T_MIN, BH_BLACKBODY_T_MAX, BH_DISC_PROFILE4_PEAK,
  BH_GAS, BH_BODY_DEPTH_CODES, bhBuildTables, bhBlackbodyLut, bhAngleOfImpact, bhShadowAngularRadius, bhDiscFlowCycleS, bhDepthSafeDistance,
  type BlackHoleTableData,
} from './SchwarzschildMath.js';

type N = any;

/** The table resolution + star sub-samples (SchwarzschildMath.BH_TABLE_SIZES). */
export type BlackHoleQuality = 'low' | 'high';

/** The caller's sky, as TSL builders along a unit WORLD direction. */
export interface BlackHoleSky {
  /** Extended light (a nebula / Milky Way photo) along `dir`, with the direction's screen derivatives for mip selection. */
  extended?: (dir: N, ddx: N, ddy: N) => N;
  /** Point sources (stars) along `dir`; the caller's own footprint filter reads fwidth(dir) — the lensed beam. `view`
   *  is the pixel's un-bent ray: fwidth(view) is the footprint the same pixel has with no hole (a caller whose stars'
   *  BRIGHTNESS depends on the footprint decides it there, so a squeezed sky keeps its light — 2026-10-08). */
  points?: (dir: N, view: N) => N;
  /** The points' mean radiance (what a beam wider than their filter carries — the photon ring's star light). */
  pointsMean?: number;
  /**
   * The caller's own BODIES (a sun, a companion star): compact sources the sky builders above cannot carry, because a
   * body has a place — at a finite distance a direction alone is not enough. Called once per pass with the pixel's ray.
   */
  sources?: (ray: BlackHoleRay) => BlackHoleSources;
}

/** One pixel's ray as the lens resolved it — what a `BlackHoleSky.sources` builder is handed (all TSL nodes). */
export interface BlackHoleRay {
  /** The unit view ray (world), un-bent. */
  view: N;
  /** The unit ESCAPE direction (world): where the ray leaves for infinity (= `view` where the hole captured it). */
  dir: N;
  /** The escape direction's screen derivatives (the lensed beam's footprint), taken at the top of the lens's Fn. */
  ddx: N; ddy: N;
  /**
   * A point of the escape ASYMPTOTE relative to the hole, in r_s: the ray leaves along the line `offset + t·dir`
   * (|offset| = the impact parameter, offset ⟂ dir; zero where captured). A source a finite distance behind the hole
   * is hit when that LINE passes through it; for one at infinity only `dir` matters.
   */
  offset: N;
}
/** What a `sources` builder returns. */
export interface BlackHoleSources {
  /** Radiance (vec3) the lens BENDS: light from beyond the hole. It is added to the sky, so the shadow swallows it and the disc covers it. */
  behind?: N;
  /** (rgb, opacity) (vec4) of a body NEARER than the hole, along the un-bent view ray: composited over the shadow and the disc. */
  front?: N;
}

export interface BlackHoleParams {
  /** The hole's centre in RENDER space (the caller's absolute − origin, computed in doubles). */
  position?: THREE.Vector3;
  /** Schwarzschild radius (m). */
  rs?: number;
  /** Draw the accretion disc. */
  disc?: boolean;
  /** The disc's spin axis (unit, world): the gas orbits counter-clockwise seen from +axis. */
  discNormal?: THREE.Vector3;
  /** Inner / outer radius of the disc in r_s (inner ≥ the ISCO, 3). */
  discInner?: number;
  discOuter?: number;
  /** The disc's hottest-ring temperature (K) at g = 1. */
  temperature?: number;
  /** Radiance (scene HDR units) of the hottest ring at g = 1 — the space exposure's curve (the sun disc is 16). */
  luminosity?: number;
  /** 1 = physical Doppler + gravitational shift and g⁴ beaming; 0 = none (the Interstellar art direction). */
  beaming?: number;
  /** The disc's angular velocity at the ISCO (rad/s, game time); Ω ∝ r^(−3/2) outward. */
  spin?: number;
  /** The gas texture's contrast: 1 = the arcs and knots of `BH_GAS`, 0 = the smooth analytic disc (the physics alone). */
  gasContrast?: number;
  /**
   * THE BODY'S SEAT: how many depth codes before the far plane the body keeps when the hole is too far to hold its own
   * (default `BH_BODY_DEPTH_CODES` 4 — at a 0.05 m near plane that is 198 km). A consumer that draws far bodies as impostors at one anchor
   * distance orders the hole among them with this: fewer codes = behind them, more = in front.
   */
  depthCodes?: number;
}

export interface BlackHoleLensOptions {
  sky: BlackHoleSky;
  /** Table resolution + star sub-samples in the body (low: 256² / 1, high: 512² / 4). */
  quality?: BlackHoleQuality;
  /** Tables built elsewhere (a worker) — skips the build. */
  tables?: BlackHoleTableData;
}

const tableCache = new Map<BlackHoleQuality, BlackHoleTableData>();
/** The tables for a quality, built once per session (~30 ms low, ~200 ms high in V8). */
export function getBlackHoleTables(q: BlackHoleQuality): BlackHoleTableData {
  let t = tableCache.get(q);
  if (!t) { t = bhBuildTables(q); tableCache.set(q, t); }
  return t;
}

function floatTexture(data: Float32Array, w: number, h: number, name: string): THREE.DataTexture {
  const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.FloatType);
  t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.flipY = false;
  t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping; t.colorSpace = THREE.NoColorSpace;
  t.name = name; t.needsUpdate = true;
  return t;
}
/** D packed as RGBA: R = Δ(e², u), G = Δ at the apsis (row 1 of the column) — one bilinear fetch serves both. */
function deflectionTexture(t: BlackHoleTableData): THREE.DataTexture {
  const { dW: W, dH: H } = t;
  const px = new Float32Array(W * H * 4);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    const o = (j * W + i) * 4;
    px[o] = t.deflection[j * W + i]; px[o + 1] = t.deflection[(H - 1) * W + i];
  }
  return floatTexture(px, W, H, 'blackhole-deflection');
}
function inverseRadiusTexture(t: BlackHoleTableData): THREE.DataTexture {
  const px = new Float32Array(t.uW * t.uH * 4);
  for (let k = 0; k < t.uW * t.uH; k++) px[k * 4] = t.inverseRadius[k];
  return floatTexture(px, t.uW, t.uH, 'blackhole-inverse-radius');
}

/** Manual bilinear fetch of a W×H table at unit coordinates (texel centres at 0 and 1) — the mirror of bhBilinear. */
function fetchBilinear(base: N, W: number, H: number, tu: N, tv: N): N {
  const x = clamp(tu, 0, 1).mul(W - 1), y = clamp(tv, 0, 1).mul(H - 1);
  const i0 = min(floor(x), float(W - 2)), j0 = min(floor(y), float(H - 2));
  const fx = x.sub(i0), fy = y.sub(j0);
  const ix = int(i0), iy = int(j0);
  const a = base.load(ivec2(ix, iy)).xy, b = base.load(ivec2(ix.add(1), iy)).xy;
  const c = base.load(ivec2(ix, iy.add(1))).xy, d = base.load(ivec2(ix.add(1), iy.add(1))).xy;
  return mix(mix(a, b, fx), mix(c, d, fx), fy);
}

const SQ23 = Math.sqrt(2 / 3), SQ13 = Math.sqrt(1 / 3);
/**
 * The debug output's offset: a material's output clamps negatives (a captured ray's −1 read back as 0), so the debug
 * channels carry Δ + 10 and u0 + 10 (−1 → 9); `g` is positive and unshifted.
 */
export const BH_DEBUG_OFFSET = 10;
/** The default spin: the inner edge turns once in ten seconds. */
const DEFAULT_SPIN = (2 * Math.PI) / 10;
/** The disc texture's flow cycle (s) at the default spin: two phases half a cycle apart, cross-faded — `bhDiscFlowCycleS` for any other. */
export const BH_DISC_FLOW_CYCLE_S = bhDiscFlowCycleS(DEFAULT_SPIN);

/** The live uniforms (one set per lens, shared by the sky pass and the body). */
function makeUniforms() {
  return {
    ex: uniform(new THREE.Vector3(0, 0, 1)),          // unit hole → camera
    u: uniform(0.01),                                  // r_s / distance
    perp: uniform(new THREE.Vector3(1, 0, 0)),         // a unit vector ⟂ ex (the degenerate ray plane)
    n: uniform(new THREE.Vector3(0, 1, 0)),            // the disc's spin axis
    dx: uniform(new THREE.Vector3(1, 0, 0)),           // the disc plane's basis
    dy: uniform(new THREE.Vector3(0, 0, -1)),
    uoc: uniform(1 / 12), uic: uniform(1 / BH_ISCO_R),
    rIn: uniform(BH_ISCO_R), rOut: uniform(12),
    disc: uniform(1),
    time: uniform(0),
    spin: uniform(DEFAULT_SPIN),
    flowCycle: uniform(BH_DISC_FLOW_CYCLE_S),         // seconds one flow phase lives (bhDiscFlowCycleS of the spin)
    gas: uniform(1),                                   // the gas texture's contrast (0 = the smooth analytic disc)
    tPeak: uniform(4500),
    lum: uniform(1),
    beaming: uniform(1),
    starFade0: uniform(0.006), starFade1: uniform(0.02),
    pointsMean: uniform(0),
    debug: uniform(0),
    // the body cap (world)
    core: uniform(0.1),                                // cap half-angle (rad)
    bodyR: uniform(1000),                              // cap radius (m) = the hole's distance, clamped inside the far plane
    holeDir: uniform(new THREE.Vector3(0, 0, -1)),     // unit camera → hole
    a1: uniform(new THREE.Vector3(1, 0, 0)), a2: uniform(new THREE.Vector3(0, 1, 0)),
  };
}
type Uniforms = ReturnType<typeof makeUniforms>;

interface LensCfg { withDisc: boolean; starSamples: number; sky: BlackHoleSky }

/**
 * The lens along the unit view ray `v` (world). Returns the radiance (vec3). The graph is the TSL twin of
 * SchwarzschildMath.bhShadePixelCpu + the shading; `debug` 1 returns (Δ or −1, g of the nearer disc hit, its u).
 */
function lensRadiance(v: N, U: Uniforms, T: { d: N; uTab: N; lut: N; dW: number; dH: number; uW: number; uH: number }, cfg: LensCfg): N {
  return Fn(() => {
    const ex = vec3(U.ex), uc = float(U.u);
    // ── the ray plane (Bruneton §3.2): x̂′ = hole → camera, ẑ′ = x̂′ × v, ŷ′ = ẑ′ × x̂′ ──
    const zr = cross(ex, v).toVar();
    const zl = length(zr).toVar();
    const ez = select(zl.greaterThan(1e-7), zr.div(max(zl, 1e-30)), vec3(U.perp)).toVar();
    const ey = cross(ez, ex).toVar();
    const radial = dot(v, ex).toVar();
    const tang = dot(v, ey).toVar();
    // THE STATIC FRAME: the observer's angle → the orbit's coordinate angle (tan δ = tan ψ / √(1 − u))
    const sq1u = sqrt(max(float(1).sub(uc), 1e-6)).toVar();
    const delta = atan(tang, radial.mul(sq1u)).toVar();
    const uDot = uc.negate().mul(radial.mul(sq1u)).div(max(tang, 1e-12)).toVar();
    const e2 = uDot.mul(uDot).add(uc.mul(uc).mul(float(1).sub(uc))).toVar();
    const bounded = e2.lessThan(BH_MU);
    // ── D(e², u): Appendix A's mapping ──
    const tuD = select(bounded,
      float(0.5).sub(sqrt(log(max(float(1).sub(e2.div(BH_MU)), 1e-30)).negate().div(50))),
      float(0.5).add(sqrt(log(max(float(1).sub(float(BH_MU).div(max(e2, 1e-30))), 1e-30)).negate().div(50)))).toVar();
    const ua = float(1 / 3).add(float(2 / 3).mul(sin(asin(clamp(e2.mul(2 / BH_MU).sub(1), -1, 1)).div(3)))).toVar();
    const xF = select(uc.lessThan(2 / 3), sqrt(max(float(2 / 3).sub(uc), 0)).negate(), sqrt(max(uc.sub(2 / 3), 0)));
    const tvD = select(bounded, float(1).sub(sqrt(max(float(1).sub(uc.div(max(ua, 1e-12))), 0))), xF.add(SQ23).div(SQ23 + SQ13)).toVar();
    const dd = fetchBilinear(T.d, T.dW, T.dH, tuD, tvD).toVar();
    const def = dd.x, defA = dd.y;
    // ── TraceRay (Bruneton Algorithm 1) ──
    const trapped = bounded.and(uc.greaterThan(2 / 3));
    const ingoing = uDot.greaterThan(0);
    const rayDef = select(trapped, float(-1), select(ingoing, select(bounded, defA.mul(2).sub(def), float(-1)), def)).toVar();
    const captured = rayDef.lessThan(0);
    // the disc's line in the ray plane: t̂ ∥ n̂ × ẑ′ with t̂·ŷ′ ≥ 0, α its angle from x̂′
    const tr = cross(vec3(U.n), ez).toVar();
    const tl = length(tr);
    const t0 = select(tl.greaterThan(1e-7), tr.div(max(tl, 1e-30)), ey);
    const tt = select(dot(t0, ey).lessThan(0), t0.negate(), t0).toVar();
    const alpha = atan(dot(tt, ey), dot(tt, ex)).toVar();
    const s = sign(uDot).toVar();
    const phi = def.add(select(s.equal(1), float(Math.PI).sub(delta), delta)).add(s.mul(alpha)).toVar();
    const phiA = defA.add(Math.PI / 2).toVar();
    const tuU = float(1).div(e2.mul(6).add(1)).toVar();
    const phiUb = e2.add(1).div(float(1 / 3).add(e2.mul(2).mul(sqrt(e2)))).toVar();
    const phi0m = mod(phi, Math.PI).toVar();
    const ui0 = fetchBilinear(T.uTab, T.uW, T.uH, tuU, phi0m.div(phiUb)).x.toVar();
    const side = s.mul(ui0.sub(uc));
    const ok0 = phi0m.lessThan(phiA).and(side.greaterThan(1e-3).or(side.greaterThan(-1e-3).and(alpha.lessThan(delta)))).and(trapped.not());
    const u0 = select(ok0, ui0, float(-1)).toVar();
    const phiB = phiA.mul(2).sub(phi).toVar();
    const phi1m = mod(phiB, Math.PI).toVar();
    const ui1 = fetchBilinear(T.uTab, T.uW, T.uH, tuU, phi1m.div(phiUb)).x.toVar();
    const ok1 = bounded.and(s.equal(1)).and(phi1m.lessThan(phiA)).and(trapped.not());
    const u1 = select(ok1, ui1, float(-1)).toVar();
    const hit0 = alpha.add(phi).sub(phi0m).toVar();
    const hit1 = alpha.add(phiB).sub(phi1m).toVar();
    // the pulse's footprint (Bruneton's anti-aliasing) — derivatives at the top, never inside a branch
    const fwUi0 = fwidth(ui0).toVar(), fwUi1 = fwidth(ui1).toVar();
    const fwU0 = fwidth(select(u0.equal(-1), u1, u0)).toVar(), fwU1 = fwidth(select(u1.equal(-1), u0, u1)).toVar();
    const fwE2 = fwidth(e2).toVar();

    // ── the escape direction and the sky along it ──
    const dPrime = select(captured, v, cos(delta.add(rayDef)).mul(ex).add(sin(delta.add(rayDef)).mul(ey))).toVar();
    const ddx = dFdx(dPrime).toVar(), ddy = dFdy(dPrime).toVar();
    const foot = max(length(ddx), length(ddy)).toVar();
    // ── the caller's own bodies (a sun): light from beyond the hole is found along the escape ASYMPTOTE — the line
    //    offset + t·dir, offset = b·(sin δ′·x̂′ − cos δ′·ŷ′) (the angular momentum b·ẑ′ is the same on the way out) — and a
    //    body nearer than the hole is drawn along the un-bent view ray, over the shadow and the disc ──
    let srcBehind: N = null, srcFront: N = null;
    if (cfg.sky.sources) {
      const dp = delta.add(rayDef);
      const bImp = float(1).div(sqrt(max(e2, 1e-12)));
      const off = select(captured, vec3(0), ex.mul(sin(dp)).sub(ey.mul(cos(dp))).mul(bImp)).toVar();
      const src = cfg.sky.sources({ view: v, dir: dPrime, ddx, ddy, offset: off });
      if (src.behind) srcBehind = vec3(src.behind).toVar();
      if (src.front) srcFront = vec4(src.front).toVar();
    }
    const ext0: N = cfg.sky.extended ? vec3(cfg.sky.extended(dPrime, ddx, ddy)) : vec3(0);
    const ext = (srcBehind ? ext0.add(srcBehind) : ext0).toVar();
    let pts: N = vec3(0);
    if (cfg.sky.points) {
      if (cfg.starSamples >= 4) {
        // four sub-beams of the pixel (±¼, ±¼): a 2×2 supersample of the stars where the lens magnifies the sky's gradient
        const o = [[-0.25, -0.25], [0.25, -0.25], [-0.25, 0.25], [0.25, 0.25]];
        let acc: N = vec3(0);
        for (const [ox, oy] of o) acc = acc.add(vec3(cfg.sky.points(normalize(dPrime.add(ddx.mul(ox)).add(ddy.mul(oy))), v)));
        pts = acc.mul(0.25);
      } else pts = vec3(cfg.sky.points(dPrime, v));
    }
    const ptsV = vec3(pts).toVar();
    // a beam wider than the stars' own filter (the photon ring maps the whole sky into a pixel) carries their MEAN —
    // the star-filter law after Bruneton §3.3.2: flux conserved, nothing sparkles
    const starW = float(1).sub(smoothstep(U.starFade0, U.starFade1, foot));
    const skyRaw = ext.add(mix(vec3(U.pointsMean), ptsV, starW));
    // the shadow's edge, anti-aliased on e² (the capture boundary is e² = μ)
    const fwE = max(fwE2, 1e-9);
    const covIn = clamp(e2.sub(BH_MU).div(fwE).add(0.5), 0, 1);
    const covOut = clamp(float(BH_MU).sub(e2).div(fwE).add(0.5), 0, 1);
    const inPS = uc.greaterThan(2 / 3);
    const cov = select(inPS, select(ingoing, float(1), covOut), select(ingoing, covIn, float(0)));
    const col = select(captured, vec3(0), skyRaw.mul(float(1).sub(cov))).toVar();

    if (cfg.withDisc) {
      // ── the disc's crossings (nearer last: it covers the farther) ──
      const c = dot(vec3(U.n), ez).toVar();
      // the flow clock: two phases half a cycle apart, each a FRESH field per cycle (its seed is the cycle's index),
      // sheared from −shear to +shear about the resting winding and cross-faded at constant contrast
      const cyc = U.time.div(U.flowCycle).toVar();
      const fA = fract(cyc), fB = fract(cyc.add(0.5));
      const wA = float(1).sub(abs(fA.mul(2).sub(1))).toVar(), wB = float(1).sub(wA).toVar();
      const wNorm = float(1).div(sqrt(wA.mul(wA).add(wB.mul(wB)))).toVar();
      const tauA = fA.sub(0.5).mul(U.flowCycle).toVar(), tauB = fB.sub(0.5).mul(U.flowCycle).toVar();
      const seedA = mod(floor(cyc), 512).mul(7.31).toVar(), seedB = mod(floor(cyc.add(0.5)), 512).mul(7.31).add(3.7).toVar();
      // THE CARRIERS: where a crossing passes from one table lookup to the other (its swept angle crossing π) the disc is
      // one continuous surface but each lookup's own hit jumps — its screen derivatives are garbage on that line (a
      // hairline of unfiltered-to-the-mean gas across the disc). The footprint is taken from the crossing that EXISTS.
      const has0 = u0.greaterThanEqual(0), has1 = u1.greaterThanEqual(0);
      const uK0 = select(has0, u0, u1).toVar(), hK0 = select(has0, hit0, hit1).toVar();
      const uK1 = select(has1, u1, u0).toVar(), hK1 = select(has1, hit1, hit0).toVar();
      /** One crossing: `uh` its inverse radius (−1 none), `fw` the pixel's footprint in u, (`uk`, `hk`) its carrier (inverse radius, polar angle in the ray plane). Returns (radiance, opacity) × the pixel's coverage. */
      const shade = (uh: N, fw: N, uk: N, hk: N): N => {
        // the pixel's footprint over the disc (Bruneton's pulse): its coverage, and the interval of u it covers
        const w = max(fw, 1e-6);
        const x0 = uh.sub(w.mul(0.5));
        const lo = max(x0, U.uoc).toVar(), hi = min(x0.add(w), U.uic).toVar();
        const span = max(hi.sub(lo), 0).toVar();
        const cov = span.div(w).mul(step(0, uh)).mul(U.disc);               // a miss (u = −1) covers nothing
        const uC = clamp(lo.add(hi).mul(0.5), U.uoc, U.uic).toVar();        // shaded at the centre of what is covered
        const rC = float(1).div(uC);
        // the crossing in the disc's plane (r_s units), its screen derivatives — taken HERE, at the top level of the Fn —
        // turned into the pixel's d(azimuth) and d(ln r): the gas texture's footprint, exact and seam-free
        const dirH = ex.mul(cos(hk)).add(ey.mul(sin(hk)));
        const q = vec2(dot(dirH, vec3(U.dx)), dot(dirH, vec3(U.dy))).div(clamp(uk, 1e-4, 1 / BH_ISCO_R)).toVar();
        const dqx = dFdx(q).toVar(), dqy = dFdy(q).toVar();
        const r2 = max(dot(q, q), 1e-8);
        const dLrX = dot(q, dqx).div(r2).toVar(), dLrY = dot(q, dqy).div(r2).toVar();
        const dAzX = q.x.mul(dqx.y).sub(q.y.mul(dqx.x)).div(r2).toVar(), dAzY = q.x.mul(dqy.y).sub(q.y.mul(dqy.x)).div(r2).toVar();
        const az = atan(q.y, q.x), lr = log(uC).negate().toVar();
        // g (Bruneton Eq. 11–14): gravitational redshift at both ends + the orbit's Doppler; the static camera's √(1 − u)
        const d23 = max(float(2).sub(uC.mul(3)), 1e-4);
        const A = sqrt(float(2).div(d23)), B = sqrt(uC.mul(uC).mul(uC).div(d23));
        const g = float(1).div(sq1u.mul(A.add(B.mul(c).div(sqrt(max(e2, 1e-12))))));
        const gB = mix(float(1), g, U.beaming).toVar();
        // T⁴ ∝ u³(1 − √(3u)): its MEAN over the covered interval (closed form — a small disc keeps its flux), the point
        // value where the interval is a sliver (the difference of two antiderivatives loses its digits there)
        const p4 = (u: N): N => u.mul(u).mul(u).mul(float(1).sub(sqrt(u.mul(3))));
        const i4 = (u: N): N => { const u2 = u.mul(u), u4 = u2.mul(u2); return u4.mul(0.25).sub(u4.mul(sqrt(u)).mul(Math.sqrt(3) / 4.5)); };
        const p4c = max(p4(uC), 0).toVar();
        const p4m = max(i4(hi).sub(i4(lo)).div(max(span, 1e-7)), 0);
        const prof4 = mix(p4c, p4m, smoothstep(1e-4, 4e-4, span)).div(BH_DISC_PROFILE4_PEAK);
        const tObs = gB.mul(pow(max(p4c.div(BH_DISC_PROFILE4_PEAK), 1e-30), 0.25)).mul(U.tPeak);
        // the blackbody chroma LUT (log T axis), manual lerp
        const lx = clamp(log(max(tObs, 1).div(BH_BLACKBODY_T_MIN)).div(Math.log(BH_BLACKBODY_T_MAX / BH_BLACKBODY_T_MIN)), 0, 1).mul(BH_BLACKBODY_LUT_SIZE - 1);
        const li = min(floor(lx), float(BH_BLACKBODY_LUT_SIZE - 2));
        const chroma = mix(T.lut.load(ivec2(int(li), int(0))).rgb, T.lut.load(ivec2(int(li).add(1), int(0))).rgb, lx.sub(li));
        // ── the gas: BH_GAS's bands in (ψ, ln r), ψ = azimuth + winding·ln r − Ω(r)·τ, each faded by ITS footprint ──
        const om = U.spin.mul(pow(rC.div(BH_ISCO_R), -1.5)).toVar();
        const phase = (tau: N, seed: N): Array<{ n: N; keep: N }> => {
          const wind = om.mul(tau).mul(1.5).add(BH_GAS.winding).toVar();    // d ψ / d ln r now (the Keplerian shear added)
          const psi = az.add(lr.mul(BH_GAS.winding)).sub(om.mul(tau));
          const cs = cos(psi).toVar(), sn = sin(psi).toVar();
          const pX = dAzX.add(wind.mul(dLrX)), pY = dAzY.add(wind.mul(dLrY));
          const pX2 = pX.mul(pX).toVar(), pY2 = pY.mul(pY).toVar(), lX2 = dLrX.mul(dLrX), lY2 = dLrY.mul(dLrY);
          return BH_GAS.bands.map(([cr, cl], j) => {
            const cells = sqrt(max(pX2.mul(cr * cr).add(lX2.mul(cl * cl)), pY2.mul(cr * cr).add(lY2.mul(cl * cl))));
            const keep = float(1).sub(smoothstep(BH_GAS.fadeLo, BH_GAS.fadeHi, cells)).toVar();
            return { n: mx_noise_float(vec3(cs.mul(cr), sn.mul(cr), lr.mul(cl).add(seed).add(j * 11.7))).mul(keep), keep };
          });
        };
        const pA = phase(tauA, seedA), pB = phase(tauB, seedB);
        let dens: N = float(1);
        BH_GAS.bands.forEach(([, , amp], j) => {
          const a = U.gas.mul(amp);
          const n = pA[j].n.mul(wA).add(pB[j].n.mul(wB)).mul(wNorm);        // the two phases, contrast kept
          // what is left of the band's variance (1 unfaded): the factor (1 + a·n)² has mean 1 + a²·σ²·left EXACTLY, so
          // the cascade keeps mean 1 at every footprint — bright knots on a dimmer ground, the disc's flux untouched
          const left = pA[j].keep.mul(wA).mul(pA[j].keep.mul(wA)).add(pB[j].keep.mul(wB).mul(pB[j].keep.mul(wB))).mul(wNorm.mul(wNorm));
          const f = n.mul(a).add(1);
          dens = dens.mul(f.mul(f).div(a.mul(a).mul(left).mul(BH_GAS.noiseVar).add(1)));
        });
        const laneCycles = max(abs(dLrX), abs(dLrY)).mul(BH_GAS.laneFreq / (2 * Math.PI));
        const lanes = sin(lr.mul(BH_GAS.laneFreq)).mul(U.gas.mul(BH_GAS.laneAmp)).mul(float(1).sub(smoothstep(0.2, 0.5, laneCycles))).add(1);
        const gas = dens.mul(lanes).toVar();
        const edge = smoothstep(U.rIn, U.rIn.mul(1.12), rC).mul(smoothstep(U.rOut, U.rOut.mul(0.8), rC));
        const g2 = gB.mul(gB);
        const L = chroma.mul(U.lum.mul(g2.mul(g2)).mul(prof4).mul(gas)).mul(edge);   // the bolometric g⁴·T⁴ — never pow(0, ·)
        const op = edge.mul(clamp(float(0.7).add(gas.mul(0.3)), 0, 1));              // optically thick: knots opaque, the thin ground nearly so
        return vec4(L.mul(cov), op.mul(cov));
      };
      const s1 = shade(u1, min(fwUi1, fwU1), uK1, hK1).toVar();
      col.assign(col.mul(float(1).sub(s1.w)).add(s1.xyz));
      const s0 = shade(u0, min(fwUi0, fwU0), uK0, hK0).toVar();
      col.assign(col.mul(float(1).sub(s0.w)).add(s0.xyz));
      // a body nearer than the hole stands in front of its disc and its shadow
      if (srcFront) col.assign(col.mul(float(1).sub(srcFront.w)).add(srcFront.xyz));
      // the debug output: (Δ or −1, g of the nearer crossing inside the disc, its u)
      const d23 = max(float(2).sub(u0.mul(3)), 1e-4);
      const g0 = float(1).div(sq1u.mul(sqrt(float(2).div(d23)).add(sqrt(u0.mul(u0).mul(u0).div(d23)).mul(c).div(sqrt(max(e2, 1e-12))))));
      const inDisc = u0.greaterThanEqual(U.uoc).and(u0.lessThanEqual(U.uic));
      return select(U.debug.greaterThan(0.5), vec3(rayDef.add(BH_DEBUG_OFFSET), select(inDisc, g0, float(0)), u0.add(BH_DEBUG_OFFSET)), col);
    }
    if (srcFront) col.assign(col.mul(float(1).sub(srcFront.w)).add(srcFront.xyz));
    return select(U.debug.greaterThan(0.5), vec3(rayDef.add(BH_DEBUG_OFFSET), 0, u0.add(BH_DEBUG_OFFSET)), col);
  })();
}

/** The body cap's geometry: a polar grid (s ∈ [0,1] from the cap's centre, ψ ∈ [0,1) around), placed by the shader. */
function capGeometry(rings = 40, segments = 96): THREE.BufferGeometry {
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  for (let i = 0; i <= rings; i++) {
    const sv = i / rings;
    for (let j = 0; j <= segments; j++) { const pv = j / segments; pos.push(sv, pv, 0); nrm.push(0, 0, 1); uv.push(sv, pv); }
  }
  const row = segments + 1;
  for (let i = 0; i < rings; i++) for (let j = 0; j < segments; j++) {
    const a = i * row + j, b = a + 1, c = a + row, d = c + 1;
    idx.push(a, c, b, b, c, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  // the shader places every vertex; a huge bound keeps any culling honest (the mesh is also frustumCulled = false)
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e12);
  return g;
}

export interface BlackHoleLensState {
  distanceM: number; u: number; rs: number; shadowRad: number; coreRad: number; bodyR: number;
  quality: BlackHoleQuality; tables: string; disc: boolean; time: number;
  /** Seconds one flow phase of the gas lives at the current spin. */
  flowCycleS: number;
}

/**
 * The lens object. Add `body` to the scene; hand `backgroundNode()` to `scene.backgroundNode`; call `update()` once per
 * frame after the camera's pose is final (before the render).
 */
export class BlackHoleLens {
  readonly body: THREE.Mesh;
  readonly quality: BlackHoleQuality;
  readonly tables: BlackHoleTableData;
  private readonly U: Uniforms;
  private readonly tex: { d: THREE.DataTexture; u: THREE.DataTexture; lut: THREE.DataTexture };
  private readonly T: { d: N; uTab: N; lut: N; dW: number; dH: number; uW: number; uH: number };
  private readonly sky: BlackHoleSky;
  private bg: N = null;
  private readonly p: Required<Omit<BlackHoleParams, 'position' | 'discNormal'>> & { position: THREE.Vector3; discNormal: THREE.Vector3 };
  private last: BlackHoleLensState;

  constructor(opts: BlackHoleLensOptions) {
    this.quality = opts.quality ?? 'high';
    this.tables = opts.tables ?? getBlackHoleTables(this.quality);
    this.sky = opts.sky;
    this.U = makeUniforms();
    this.U.pointsMean.value = opts.sky.pointsMean ?? 0;
    const lut = floatTexture(bhBlackbodyLut(), BH_BLACKBODY_LUT_SIZE, 1, 'blackhole-blackbody');
    this.tex = { d: deflectionTexture(this.tables), u: inverseRadiusTexture(this.tables), lut };
    this.T = {
      d: texture(this.tex.d), uTab: texture(this.tex.u), lut: texture(this.tex.lut),
      dW: this.tables.dW, dH: this.tables.dH, uW: this.tables.uW, uH: this.tables.uH,
    };
    // temperature 4500 K / luminosity 1 (was 6500 / 3): the realism pass's defaults — the references draw the disc WARM,
    // and at 3 the display curve flattened the approaching side to white (Atlas's DISC_DEFAULTS carries the measurements)
    this.p = {
      position: new THREE.Vector3(), rs: 1000, disc: true, discNormal: new THREE.Vector3(0, 1, 0), discInner: BH_ISCO_R,
      discOuter: 12, temperature: 4500, luminosity: 1, beaming: 1, spin: DEFAULT_SPIN, gasContrast: 1, depthCodes: BH_BODY_DEPTH_CODES,
    };

    // ── the body: a camera-centred cap at the hole's distance ──
    const U = this.U;
    const mat = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, depthWrite: true, depthTest: true, transparent: false });
    mat.name = 'blackhole-body';
    mat.fog = false;
    mat.positionNode = Fn(() => {
      const th = positionGeometry.x.mul(U.core);
      const ps = positionGeometry.y.mul(2 * Math.PI);
      const dir = vec3(U.holeDir).mul(cos(th)).add(vec3(U.a1).mul(cos(ps)).add(vec3(U.a2).mul(sin(ps))).mul(sin(th)));
      const p = cameraPosition.add(dir.mul(U.bodyR));
      positionPrevious.assign(p); // a still hole moves on screen only by the camera's own motion (the positionPrevious LAW)
      return p;
    })();
    mat.colorNode = vec4(lensRadiance(normalize(positionWorld.sub(cameraPosition)), U, this.T, { withDisc: true, starSamples: this.quality === 'high' ? 4 : 1, sky: this.sky }), 1);
    const body = new THREE.Mesh(capGeometry(), mat);
    body.name = 'blackhole-body';
    body.frustumCulled = false;
    body.renderOrder = -100; // before every other opaque: what lies behind the hole fails the depth test against it
    body.matrixAutoUpdate = false;
    body.matrixWorldAutoUpdate = false; // the shader writes WORLD positions; the model matrix must stay identity
    body.matrix.identity(); body.matrixWorld.identity();
    this.body = body;
    this.last = { distanceM: 0, u: 0, rs: 0, shadowRad: 0, coreRad: 0, bodyR: 0, quality: this.quality, tables: `${this.tables.dW}x${this.tables.dH}/${this.tables.uW}x${this.tables.uH}`, disc: true, time: 0, flowCycleS: U.flowCycle.value };
  }

  /** The lensed sky for `scene.backgroundNode` (built once). */
  backgroundNode(): N {
    if (!this.bg) this.bg = lensRadiance(positionWorldDirection, this.U, this.T, { withDisc: false, starSamples: 1, sky: this.sky });
    return this.bg;
  }

  /** Set any parameter (render-space position, r_s, the disc). */
  set(params: BlackHoleParams): void {
    const p = this.p;
    if (params.position) p.position.copy(params.position);
    if (params.discNormal) p.discNormal.copy(params.discNormal).normalize();
    for (const k of ['rs', 'disc', 'discInner', 'discOuter', 'temperature', 'luminosity', 'beaming', 'spin', 'gasContrast', 'depthCodes'] as const) {
      const v = params[k];
      if (v !== undefined) (p as Record<string, unknown>)[k] = v;
    }
    p.discInner = Math.max(BH_ISCO_R, p.discInner);
    p.discOuter = Math.max(p.discInner * 1.05, p.discOuter);
    const U = this.U;
    U.n.value.copy(p.discNormal);
    // the disc plane's basis (any orthonormal pair ⟂ n — the texture's azimuth origin)
    const ref = Math.abs(p.discNormal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    U.dx.value.crossVectors(ref, p.discNormal).normalize();
    U.dy.value.crossVectors(p.discNormal, U.dx.value).normalize();
    U.uic.value = 1 / p.discInner; U.uoc.value = 1 / p.discOuter;
    U.rIn.value = p.discInner; U.rOut.value = p.discOuter;
    U.disc.value = p.disc ? 1 : 0;
    U.tPeak.value = p.temperature; U.lum.value = p.luminosity; U.beaming.value = Math.max(0, Math.min(1, p.beaming)); U.spin.value = p.spin;
    U.flowCycle.value = bhDiscFlowCycleS(p.spin);
    U.gas.value = Math.max(0, Math.min(1, Number.isFinite(p.gasContrast) ? p.gasContrast : 1));
  }

  /** The debug output: 1 = (Δ or −1, g of the nearer disc crossing, its u) instead of radiance (the bed's GPU-vs-CPU pin); channels 1 and 3 carry + BH_DEBUG_OFFSET. */
  setDebug(on: boolean): void { this.U.debug.value = on ? 1 : 0; }
  /** Freeze / set the disc's animation clock (s). */
  setTime(t: number): void { this.U.time.value = t; }
  /** The points' mean for beams wider than their filter, when it follows the display (a non-finite or negative value is ignored). */
  setPointsMean(v: number): void { if (Number.isFinite(v) && v >= 0) this.U.pointsMean.value = v; }

  /**
   * Per frame, after the camera's pose is final: the camera's radius and axis, the body cap's extent (the shadow + the
   * disc's images, never the whole screen unless the camera is inside that zone), the disc clock.
   */
  update(camera: THREE.Camera, dtSec: number, viewportHeightPx = 1080, cameraWorld?: THREE.Vector3): BlackHoleLensState {
    const U = this.U, p = this.p;
    // the caller may hand the frame's final camera position (a rig whose matrixWorld updates only at render time)
    const cam = cameraWorld ? cameraWorld.clone() : new THREE.Vector3().setFromMatrixPosition(camera.matrixWorld);
    const rel = cam.clone().sub(p.position);                 // doubles: render space, camera-relative
    const dist = Math.max(rel.length(), p.rs * 1.02);
    const u = Math.min(0.98, p.rs / dist);
    const ex = rel.lengthSq() > 0 ? rel.divideScalar(rel.length()) : new THREE.Vector3(0, 0, 1);
    U.ex.value.copy(ex); U.u.value = u;
    const perp = Math.abs(ex.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    U.perp.value.copy(perp).addScaledVector(ex, -perp.dot(ex)).normalize();
    U.holeDir.value.copy(ex).negate();
    U.a1.value.copy(U.perp.value);
    U.a2.value.crossVectors(U.holeDir.value, U.a1.value).normalize();
    U.time.value += Math.max(0, dtSec);
    // the body's extent: the shadow + the photon ring, or the disc's direct image (b ≲ r_out + 2, the far side's lifted
    // image sits inside that) — plus four pixels of anti-aliasing margin
    const fov = (camera as THREE.PerspectiveCamera).fov ?? 60;
    const pix = (2 * Math.tan(((fov * Math.PI) / 180) / 2)) / Math.max(1, viewportHeightPx);
    const bCore = p.disc ? Math.max(BH_CRITICAL_B * 1.35, p.discOuter * 1.12 + 2) : BH_CRITICAL_B * 1.35;
    const core = Math.min(Math.PI, Math.max(bhAngleOfImpact(u, bCore), bhShadowAngularRadius(u) * 1.2) + 4 * pix);
    U.core.value = core;
    const far = (camera as THREE.PerspectiveCamera).far ?? 1e7, near = (camera as THREE.PerspectiveCamera).near ?? 0.1;
    // THE BODY'S SEAT: never where the cap shares the far plane's depth code — a sky pass drawn after the opaques and
    // depth-tested there painted over it (the sector's "QR code" disc). Reversed depth has codes to spare out there.
    const reversed = (camera as unknown as { reversedDepth?: boolean }).reversedDepth === true;
    U.bodyR.value = Math.min(dist, far * 0.45, reversed ? Infinity : bhDepthSafeDistance(near, far, Math.max(1, Number.isFinite(p.depthCodes) ? p.depthCodes : BH_BODY_DEPTH_CODES)));
    this.last = {
      distanceM: dist, u, rs: p.rs, shadowRad: bhShadowAngularRadius(u), coreRad: core, bodyR: U.bodyR.value,
      quality: this.quality, tables: `${this.tables.dW}x${this.tables.dH}/${this.tables.uW}x${this.tables.uH}`, disc: p.disc, time: U.time.value,
      flowCycleS: U.flowCycle.value,
    };
    return this.last;
  }

  state(): BlackHoleLensState { return this.last; }

  dispose(): void {
    this.body.removeFromParent();
    this.body.geometry.dispose();
    (this.body.material as THREE.Material).dispose();
    this.tex.d.dispose(); this.tex.u.dispose(); this.tex.lut.dispose();
  }
}
